import { Checkbox, FormControlLabel, FormGroup, FormLabel, Grid, MenuItem, Select, TextField } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Company, GetCompanies } from "src/apis/company";
import { createEezycoinOffering, CreateEezycoinOfferingPayload, getEezycoinOfferingById, updateEezycoinOffering } from "src/apis/eezycoin";
import Form from "src/pages/components/Form";
import FormSaveAndResetButtons from "src/pages/components/FormSaveAndResetButtons";
import PageContainer from "src/pages/components/PageContainer";
import { isUserAdministrator } from "src/utils/auth";
import { isSuccessOrAllRequiredFieldsAreFilled } from "src/utils/form";
import { eezycoinApiToFormValues as eezycoinOfferingToFormValues } from "./logic/eezycoin_offerings";

enum OfferType {
  EEZYFAMILY,
  COMPANY,
}

const defaultOfferType = OfferType.EEZYFAMILY

const initialValues: CreateEezycoinOfferingPayload = {
  coins_included: 1,
  company_id: null,
  description: '',
  is_active: true,
  is_eezyfamily_offer: (defaultOfferType === OfferType.EEZYFAMILY),
  title: '',
  total_value: 0,
}

const alwaysOptionalFields: string[] = [
  'description',
  'is_active',
]

const areFormValuesValid = (success: boolean, formValues: CreateEezycoinOfferingPayload): boolean => {
  const optionalFields = (formValues?.is_eezyfamily_offer)
      ? [...alwaysOptionalFields, "company_id"]
      : [...alwaysOptionalFields, "is_eezyfamily_offer"]

  return isSuccessOrAllRequiredFieldsAreFilled(success, formValues, optionalFields)
}

export default function EezycoinOfferingsFormPage() {
  const [error, setError] = useState<string>('')
  const [success, setSuccess] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [formValues, setFormValues] = useState<CreateEezycoinOfferingPayload>(initialValues);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [offerType, setOfferType] = useState<OfferType>(defaultOfferType);

  const { id } = useParams()

  const isEdit = useMemo(() => Boolean(id), [id])
  const valuePerCoin = useMemo(
    () => (formValues.total_value / formValues.coins_included).toFixed(2),
    [formValues]
  )
  const showValuePerCoinHelper = useMemo(
    () => (formValues.total_value > 0 && formValues.coins_included > 1),
    [formValues]
  )
  const isSubmitDisabled = useMemo(
    (): boolean => areFormValuesValid(success, formValues),
    [success, formValues]
  )

  const fetchCompanies = useCallback(async (): Promise<void> => {
    const res = await GetCompanies()
    if (res?.data?.length > 0) {
      setCompanies(res?.data)
    }
  }, [])

  const fetchDefaultFormValues = useCallback(async (): Promise<void> => {
    const offering = await getEezycoinOfferingById(id)
    if (offering) {
      setFormValues(eezycoinOfferingToFormValues(offering))

      setOfferType(_previous => {
        if (offering?.is_eezyfamily_offer) {
          return OfferType.EEZYFAMILY
        }

        return OfferType.COMPANY
      })
    }
  }, [id])

  const fetchData = useCallback(async (): Promise<void> => {
    setLoading(true)

    try {
      await fetchCompanies()

      if (isEdit) {
        await fetchDefaultFormValues()
      }

      setError('')
    } catch (err: any) {
      setError(err?.message)
    } finally {
      setLoading(false)
    }
  }, [isEdit, fetchCompanies, fetchDefaultFormValues])

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault()
    setLoading(true)

    const payload = formValues
    if (payload?.is_eezyfamily_offer) {
      payload.company_id = null
    }

    try {
      if (isEdit) {
        await updateEezycoinOffering(id, payload)
      } else {
        await createEezycoinOffering(payload)
      }

      setError('')
      setSuccess(true)
    } catch (err: any) {
      setError(err?.message)
      setSuccess(false)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return (
    <PageContainer
      title={isEdit ? "Editando oferta" : "Adicionar oferta"}
      error={error}
      guard={isUserAdministrator}
    >
      <Form handleSubmit={handleSubmit}>
        <FormGroup>
          <FormLabel>
            Título da oferta
          </FormLabel>
          <TextField
            type="text"
            value={formValues.title}
            onChange={(e) => {
              setFormValues(previous => ({
                ...previous,
                title: e.target.value,
              }))
            }}
          />
        </FormGroup>

        <Grid  container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormGroup>
              <FormLabel>
                Coins inclusas
              </FormLabel>
              <TextField
                type="number"
                value={formValues.coins_included}
                onChange={(e) => {
                  const coins = parseInt(e.target.value) > 1
                    ? parseInt(e.target.value)
                    : 1

                  setFormValues(previous => ({
                    ...previous,
                    coins_included: coins,
                  }))
                }}
              />
            </FormGroup>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormGroup>
              <FormLabel>
                Valor total em reais
              </FormLabel>
              <TextField
                type="number"
                value={formValues.total_value}
                helperText={showValuePerCoinHelper && `Cerca de R$ ${valuePerCoin} por coin`}
                onChange={(e) => {
                  setFormValues(previous => ({
                    ...previous,
                    total_value: parseFloat(e.target.value),
                  }))
                }}
              />
            </FormGroup>
          </Grid>
        </Grid>

        <FormGroup>
          <FormLabel>
            Público alvo da oferta
          </FormLabel>
          <Select
            value={offerType}
            onChange={(e) => {
              const oType = e?.target?.value as OfferType;

              setOfferType(oType)
              setFormValues(previous => ({
                ...previous,
                is_eezyfamily_offer: (oType === OfferType.EEZYFAMILY),
              }))
            }}
          >
            <MenuItem value={OfferType.EEZYFAMILY}>
              Eezyfamily
            </MenuItem>
            <MenuItem value={OfferType.COMPANY}>
              Empresa
            </MenuItem>
          </Select>
        </FormGroup>

        {(offerType === OfferType.COMPANY) && (
          <FormGroup>
            <FormLabel>
              Empresa
            </FormLabel>
            <Select
              value={formValues?.company_id}
              onChange={(e) => {
                const companyId = String(e?.target?.value);

                setFormValues(previous => ({
                  ...previous,
                  company_id: companyId === "0" ? null : parseInt(companyId),
                }))
              }}
            >
              <MenuItem value={0}>
                Selecione uma empresa...
              </MenuItem>
              {companies?.map(c => (
                <MenuItem key={c?.id} value={c?.id}>
                  {c?.trade_name}
                </MenuItem>
              ))}
            </Select>
          </FormGroup>
        )}

        <FormGroup>
          <FormControlLabel
            control={<Checkbox
              checked={formValues.is_active}
              onChange={(e) => {
                setFormValues(previous => ({
                  ...previous,
                  is_active: e.target.checked,
                }))
              }}
            />}
            label="Oferta ativa?"
          />
          <FormControlLabel
            control={<Checkbox
              disabled
              checked={formValues.coins_included > 1}
            />}
            label="Bundle?"
          />
        </FormGroup>

        <FormSaveAndResetButtons
          success={success}
          loading={loading}
          isSubmitDisabled={isSubmitDisabled}
          isResetFormStateDisabled={isEdit}
          resetFormState={() => setFormValues(initialValues)}
        />
      </Form>
    </PageContainer>
  )
}
